'use client';

import React from 'react';
import Link from 'next/link';

// Metadata is now exported as an object (Next.js Metadata API)
// Note: Metadata export must be used in Server Components
// For client components, use dynamic metadata via generateMetadata or move to server component
const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb Navigation */}
      <div className="bg-institutional-grey border-b border-grey-200">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
            <Link href="/homepage" className="text-institutional hover:text-institutional-dark transition-fast">
              Home
            </Link>
            <span className="text-grey-400">/</span>
            <span className="text-grey-600">Privacy Policy</span>
          </nav>
        </div>
      </div>

      {/* Header Section */}
      <div className="bg-institutional py-16 lg:py-20">
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4 text-white">Privacy Policy</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            How we handle information you provide when engaging with Diaax services.
          </p>
          <p className="text-sm text-white/70 mt-6">
            Last Updated: December 17, 2025
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="max-w-4xl mx-auto">
          
          {/* Quick Navigation */}
          <div className="bg-institutional-grey border border-grey-200 rounded-lg p-6 mb-12">
            <h2 className="text-lg font-semibold text-institutional mb-4">Quick Navigation</h2>
            <ul className="space-y-2">
              <li>
                <a href="#section-1" className="text-institutional hover:text-institutional-dark transition-fast">
                  1. Information We Collect
                </a>
              </li>
              <li>
                <a href="#section-2" className="text-institutional hover:text-institutional-dark transition-fast">
                  2. How We Use Your Information
                </a>
              </li>
              <li>
                <a href="#section-3" className="text-institutional hover:text-institutional-dark transition-fast">
                  3. Data Storage and Security
                </a>
              </li>
              <li>
                <a href="#section-4" className="text-institutional hover:text-institutional-dark transition-fast">
                  4. Third-Party Services
                </a>
              </li>
              <li>
                <a href="#section-5" className="text-institutional hover:text-institutional-dark transition-fast">
                  5. Your Rights and Choices
                </a>
              </li>
              <li>
                <a href="#section-6" className="text-institutional hover:text-institutional-dark transition-fast">
                  6. Data Retention
                </a>
              </li>
              <li>
                <a href="#section-7" className="text-institutional hover:text-institutional-dark transition-fast">
                  7. International Data Transfers
                </a>
              </li>
              <li>
                <a href="#section-8" className="text-institutional hover:text-institutional-dark transition-fast">
                  8. Contact Information
                </a>
              </li>
            </ul>
          </div>

          {/* Section 1 */}
          <section id="section-1" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">1. Information We Collect</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">1.1 Personal Information</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  When you engage with Diaax through our contact forms or communication channels, we collect:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Full name and professional title</li>
                  <li>Email address and telephone contact details</li>
                  <li>Organization name and sector affiliation</li>
                  <li>Message content and consultation inquiries</li>
                  <li>Professional background relevant to institutional advisory services</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">1.2 Technical Information</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Our website automatically collects certain technical data to improve user experience:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>IP addresses and geographic location data</li>
                  <li>Browser type, version, and device information</li>
                  <li>Pages visited, time spent, and navigation patterns</li>
                  <li>Referral sources and access timestamps</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">2. How We Use Your Information</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">2.1 Primary Purposes</h3>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Responding to consultation requests and strategic inquiries</li>
                  <li>Evaluating institutional advisory engagement opportunities</li>
                  <li>Providing information about our services and methodologies</li>
                  <li>Facilitating strategic conversations and professional communication</li>
                  <li>Maintaining professional communication records</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">2.2 Communication Preferences</h3>
                <p className="text-grey-700 leading-relaxed">
                  We respect institutional communication protocols and will only contact you regarding matters directly related to your inquiry or ongoing advisory engagements. You may opt out of non-essential communications at any time.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="section-3" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">3. Data Storage and Security</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">3.1 Security Measures</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  We follow standard data protection practices:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Secure data transmission</li>
                  <li>Cloud storage with access controls</li>
                  <li>Standard confidentiality protocols</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">3.2 Data Protection Standards</h3>
                <p className="text-grey-700 leading-relaxed">
                  Our data handling practices follow international data protection regulations including GDPR principles, providing standard privacy protections for professional communications.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="section-4" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">4. Third-Party Services</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">4.1 Service Providers</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Diaax utilizes select third-party service providers to support our operations:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Cloud hosting and infrastructure providers</li>
                  <li>Email communication platforms</li>
                  <li>Analytics tools for website performance monitoring</li>
                  <li>Professional communication and scheduling systems</li>
                </ul>
                <p className="text-grey-700 leading-relaxed mt-4">
                  All third-party providers are selected based on standard security and privacy practices for professional services.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">4.2 Cookies and Tracking</h3>
                <p className="text-grey-700 leading-relaxed">
                  Our website uses essential cookies for functionality and optional analytics cookies to improve user experience. You can manage cookie preferences through your browser settings. We do not engage in behavioral advertising or cross-site tracking.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">5. Your Rights and Choices</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">5.1 Data Subject Rights</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  You have standard rights regarding your personal information:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li><strong>Access:</strong> Request copies of personal data we hold</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong>Deletion:</strong> Request removal of personal data (subject to legal obligations)</li>
                  <li><strong>Portability:</strong> Obtain data in structured format</li>
                  <li><strong>Objection:</strong> Object to specific data processing activities</li>
                  <li><strong>Restriction:</strong> Request temporary suspension of data processing</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">5.2 Exercising Your Rights</h3>
                <p className="text-grey-700 leading-relaxed">
                  To exercise any of these rights, please contact us at <a href="mailto:info@diaax.com" className="text-institutional hover:text-institutional-dark underline">info@diaax.com</a>. We aim to respond to verified requests within 30 days and provide clear explanations for any limitations based on legal obligations or legitimate business interests.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section id="section-6" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">6. Data Retention</h2>
            
            <div className="space-y-4">
              <p className="text-grey-700 leading-relaxed">
                Diaax retains personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy or as required by applicable legal and regulatory obligations.
              </p>
              
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">Retention Periods</h3>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li><strong>Inquiry Data:</strong> 24 months from last contact unless ongoing engagement exists</li>
                  <li><strong>Advisory Engagement Records:</strong> 7 years following engagement completion for professional liability purposes</li>
                  <li><strong>Technical Logs:</strong> 12 months for security and performance analysis</li>
                  <li><strong>Communication Records:</strong> Duration determined by institutional relationship status</li>
                </ul>
              </div>

              <p className="text-grey-700 leading-relaxed mt-4">
                Following retention period expiration, data is securely deleted or anonymized to prevent identification.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section id="section-7" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">7. International Data Transfers</h2>
            
            <div className="space-y-4">
              <p className="text-grey-700 leading-relaxed">
                As an advisory firm serving institutions globally, Diaax may transfer personal information across borders as necessary for service delivery.
              </p>
              
              <div>
                <h3 className="text-xl font-semibold text-institutional-dark mb-3">7.1 Transfer Safeguards</h3>
                <p className="text-grey-700 leading-relaxed mb-4">
                  Cross-border data transfers follow standard international practices:
                </p>
                <ul className="list-disc list-inside space-y-2 text-grey-700 ml-4">
                  <li>Standard contractual clauses where applicable</li>
                  <li>Adequacy determinations where relevant</li>
                  <li>Appropriate consent for sensitive information transfers</li>
                  <li>Encryption and secure transmission protocols</li>
                </ul>
              </div>

              <p className="text-grey-700 leading-relaxed">
                International data transfers are conducted in accordance with applicable data protection regulations.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section id="section-8" className="mb-12">
            <h2 className="text-2xl font-bold text-institutional mb-6">8. Contact Information</h2>
            
            <div className="bg-institutional-grey border border-grey-200 rounded-lg p-6 space-y-4">
              <p className="text-grey-700 leading-relaxed">
                For privacy-related inquiries, data subject requests, or concerns about how your information is handled:
              </p>
              
              <div className="space-y-2">
                <p className="text-institutional-dark font-semibold">Data Protection Contact</p>
                <p className="text-grey-700">Diaax Ltd</p>
                <p className="text-grey-700">
                  Email: <a href="mailto:info@diaax.com" className="text-institutional hover:text-institutional-dark underline">info@diaax.com</a>
                </p>
              </div>

              <p className="text-grey-700 leading-relaxed text-sm mt-4">
                We are committed to addressing privacy concerns promptly and transparently, consistent with institutional standards for data protection and professional advisory ethics.
              </p>
            </div>
          </section>

          {/* Policy Updates Notice */}
          <div className="border-t border-grey-200 pt-8 mt-12">
            <h3 className="text-xl font-semibold text-institutional-dark mb-4">Policy Updates</h3>
            <p className="text-grey-700 leading-relaxed">
              This Privacy Policy may be updated periodically to reflect changes in our practices, legal requirements, or service offerings. Material changes will be communicated through prominent website notices or direct communication to institutional stakeholders with whom we maintain active relationships. Continued use of our services following policy updates constitutes acceptance of revised terms.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;